/**
 * DocumentExpiryAlert Component
 * 
 * Componente para exibir alertas de documentos prestes a vencer.
 * 
 * Funcionalidades:
 * - Lista documentos prestes a vencer
 * - Cores por urgência
 * - Links para renovação/documentos
 */

import { AlertTriangle, ChevronRight } from 'lucide-react'
import './DocumentExpiryAlert.css'

function DocumentExpiryAlert({
  documents = [],
  onDocumentClick,
  className = '',
  maxItems = 10
}) {
  if (!documents || documents.length === 0) {
    return null
  }

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return null

    const expiry = new Date(expiryDate)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    expiry.setHours(0, 0, 0, 0)

    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))

    if (daysUntilExpiry < 0) {
      return { variant: 'expired', label: 'Vencido', days: Math.abs(daysUntilExpiry) }
    } else if (daysUntilExpiry <= 7) {
      return { variant: 'urgent', label: 'Vence em', days: daysUntilExpiry }
    } else if (daysUntilExpiry <= 30) {
      return { variant: 'warning', label: 'Vence em', days: daysUntilExpiry }
    }

    return null
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR')
  }

  const sortedDocuments = [...documents]
    .map(doc => ({
      ...doc,
      expiryStatus: getExpiryStatus(doc.expiry_date)
    }))
    .filter(doc => doc.expiryStatus !== null)
    .sort((a, b) => {
      // Ordenar por urgência: vencidos primeiro, depois por dias restantes
      if (a.expiryStatus.variant === 'expired' && b.expiryStatus.variant !== 'expired') return -1
      if (a.expiryStatus.variant !== 'expired' && b.expiryStatus.variant === 'expired') return 1
      return a.expiryStatus.days - b.expiryStatus.days
    })
    .slice(0, maxItems)

  if (sortedDocuments.length === 0) {
    return null
  }

  return (
    <div className={`document-expiry-alert ${className}`}>
      <div className="document-expiry-alert-header">
        <h3 className="document-expiry-alert-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertTriangle size={20} />
          Documentos Prestes a Vencer
        </h3>
      </div>

      <div className="document-expiry-alert-list">
        {sortedDocuments.map((document) => {
          const status = document.expiryStatus
          return (
            <div
              key={document.id}
              className={`document-expiry-alert-item document-expiry-alert-item-${status.variant}`}
              onClick={() => onDocumentClick?.(document)}
            >
              <div className="document-expiry-alert-item-content">
                <div className="document-expiry-alert-item-header">
                  <span className="document-expiry-alert-item-title">
                    {document.title || 'Sem título'}
                  </span>
                  <span className={`document-expiry-alert-item-badge document-expiry-alert-item-badge-${status.variant}`}>
                    {status.label} {status.days} {status.days === 1 ? 'dia' : 'dias'}
                  </span>
                </div>
                
                {document.category && (
                  <span className="document-expiry-alert-item-category">
                    {document.category.name}
                  </span>
                )}

                <div className="document-expiry-alert-item-meta">
                  <span>Vence em: {formatDate(document.expiry_date)}</span>
                </div>
              </div>
              
              <div className="document-expiry-alert-item-action" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChevronRight size={20} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DocumentExpiryAlert

